import { useEffect, useRef, useState } from 'react';
import YouTube, { YouTubeProps } from 'react-youtube';
import '../../../assets/contentYoutubeVideo.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, useTheme } from '@mui/material';
import { ContentFeedSection, ContentInteractionAction, MetaItem } from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { Logger } from '../../../config/logging/logger';
import { useYoutubeMobileFullScreen } from './ContentYoutubeVideo/useYoutubeMobileFullScreen';

export interface ContentYoutubePlayerProps {
  videoId: string;
  contentId: number;
  memberId: string;
  contentTitle?: string;
  contentCreatorName?: string | null
  title?: string | null,
  heading?: string,
  contentMetaData?: MetaItem[],
  contentFeedSection?: ContentFeedSection,
  contentFeedSectionPosition?: number,
  thumbnailUrl?: string,
}

export default function ContentYoutubePlayer(
  {
    videoId, contentId, contentCreatorName,
    title, heading, contentMetaData,
    contentFeedSection, contentFeedSectionPosition,
    thumbnailUrl,
  }: ContentYoutubePlayerProps,
) {
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setInterval>
  | undefined>(undefined);
  const { trackContentInteraction } = useCustomEventTracking();
  const [isStarted, setStarted] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const theme = useTheme();
  const hasAutoplayedRef = useRef(false);
  const computedThumbnailUrl = thumbnailUrl || (
    videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '');
  const logger = Logger.create('ContentYoutubePlayer');
  const { requestFullScreen } = useYoutubeMobileFullScreen();

  useEffect(
    () => () => {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onPlayerStateChange: YouTubeProps['onStateChange'] = async (event) => {
    const { data, target } = event;

    if (data === YouTube.PlayerState.PLAYING) {
      if (!isStarted) {
        logger.debug(Logger.format('Video started playing'));
        setStarted(true);
        const PlayDurationTime = await target.getDuration();

        requestFullScreen(target);

        trackContentInteraction(
          ContentInteractionAction.Play,
          title?.replace(/^\w/, (c) => c.toUpperCase()) || '',
          {
            contentId,
            contentTitle: heading || '',
            contentCreatorName: contentCreatorName || '',
            contentMetaData: contentMetaData || [],
            type: title || '',
            progress: '0%',
            videoDurationInSeconds: PlayDurationTime.toFixed(2),
            ...(contentFeedSection && { contentFeedSection }),
            ...(contentFeedSectionPosition && { contentFeedSectionPosition }),
          },
        );
      }

      setIntervalId(setInterval(async () => {
        const playCurrentTime = await target.getCurrentTime();
        const PlayDurationTime = await target.getDuration();
        trackContentInteraction(
          ContentInteractionAction.Play,
          title?.replace(/^\w/, (c) => c.toUpperCase()) || '',
          {
            contentId,
            contentTitle: heading || '',
            contentCreatorName: contentCreatorName || '',
            contentMetaData: contentMetaData || [],
            type: title || '',
            playedSeconds: playCurrentTime.toFixed(2),
            videoDurationInSeconds: PlayDurationTime.toFixed(2),
            progress: `${((playCurrentTime / PlayDurationTime) * 100).toFixed(2)}%`,
            ...(contentFeedSection && { contentFeedSection }),
            ...(contentFeedSectionPosition && { contentFeedSectionPosition }),
          },
        );
      }, 10000)); // every 10 seconds
    } else {
      const currentTime = await target.getCurrentTime();
      const durationTime = await target.getDuration();

      if (data === YouTube.PlayerState.PAUSED) {
        logger.debug(Logger.format('Video paused'));
        trackContentInteraction(
          ContentInteractionAction.Pause,
          title?.replace(/^\w/, (c) => c.toUpperCase()) || '',
          {
            contentId,
            contentTitle: heading || '',
            contentCreatorName: contentCreatorName || '',
            contentMetaData: contentMetaData || [],
            type: title || '',
            playedSeconds: currentTime.toFixed(2),
            videoDurationInSeconds: durationTime.toFixed(2),
            progress: `${((currentTime / durationTime) * 100).toFixed(2)}%`,
            ...(contentFeedSection && { contentFeedSection }),
            ...(contentFeedSectionPosition && { contentFeedSectionPosition }),
          },
        );

        clearInterval(intervalId);
        setIntervalId(undefined);
      } else if (data === YouTube.PlayerState.ENDED) {
        logger.debug(Logger.format('Video ended'));

        trackContentInteraction(
          ContentInteractionAction.Play,
          title?.replace(/^\w/, (c) => c.toUpperCase()) || '',
          {
            contentId,
            contentTitle: heading || '',
            contentCreatorName: contentCreatorName || '',
            contentMetaData: contentMetaData || [],
            type: title || '',
            videoDurationInSeconds: durationTime.toFixed(2),
            progress: '100%',
            ...(contentFeedSection && { contentFeedSection }),
            ...(contentFeedSectionPosition && { contentFeedSectionPosition }),
          },
        );

        clearInterval(intervalId);
        setIntervalId(undefined);
      }
    }
  };

  return (
    <>
      {!showPlayer ? (
        <Box
          onClick={() => setShowPlayer(true)}
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%',
            borderRadius: theme.spacing(2),
            backgroundColor: theme.palette.common.black,
            backgroundImage: `url(${computedThumbnailUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#FF0000', // YouTube red
              borderRadius: theme.spacing(1.5), // rounded rectangle
              padding: `${theme.spacing(1.5)} ${theme.spacing(2.25)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
            }}
          >
            <PlayArrowIcon sx={{ color: theme.palette.common.white, fontSize: 24 }} />
          </Box>
        </Box>
      ) : (
        <YouTube
          videoId={videoId}
          style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}
          iframeClassName={'YoutubeIframe'}
          onStateChange={onPlayerStateChange}
          onReady={(event) => {
            if (!hasAutoplayedRef.current) {
              event.target.mute(); // This ensures the video is muted
              event.target.playVideo(); // This triggers autoplaye
              hasAutoplayedRef.current = true;
            }
          }}
          opts={{
            playerVars: {
              autoplay: 1,
              rel: 0,
            },
          }}
        />
      )}
    </>
  );
}
